import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { EcoAvatar, EcoMark } from './EcoAvatar.js'
import './eco.css'

/**
 * Eco's buttons, from the chat-button kit. Every control that asks the AI for
 * something is one of these — the job card's Assistant tab, the compose
 * window's Assistant, writing help on a proposal, talking a report in, Eco
 * itself — so the AI reads as Eco's wherever it turns up. The styles are the
 * `.ecb` rules in `eco.css`. Decision 116.
 */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Charcoal surfaces: the command bar, the rail, a dark toolbar. */
  dark?: boolean
}

/** The pill: the frog and a word, or the frog alone with `label` as its name. */
export const EcoButton = forwardRef<
  HTMLButtonElement,
  ButtonProps & { size?: 'md' | 'sm'; label?: string; children?: ReactNode }
>(function EcoButton({ size = 'md', label, dark, className, children, type = 'button', ...props }, ref) {
  const only = children === undefined || children === null
  return (
    <button
      ref={ref}
      type={type}
      aria-label={only ? label : props['aria-label']}
      title={only ? label : props.title}
      data-theme={dark ? 'dark' : undefined}
      className={cn('ecb ecb-eco', size === 'sm' && 'ecb-sm', only && 'ecb-only', className)}
      {...props}
    >
      <EcoMark className={size === 'sm' ? 'size-5' : 'size-6'} />
      {only ? null : <span>{children}</span>}
    </button>
  )
})

/** A suggestion to tap. */
export function EcoChip({ dark, className, type = 'button', ...props }: ButtonProps) {
  return <button type={type} data-theme={dark ? 'dark' : undefined} className={cn('ecb ecb-chip', className)} {...props} />
}

const BADGES = {
  voice: <path d="M3 6v4m5-7v10m5-7v4" strokeWidth="1.8" strokeLinecap="round" />,
  spark: <path d="m8 2 1.5 4.5L14 8l-4.5 1.5L8 14 6.5 9.5 2 8l4.5-1.5Z" strokeWidth="1.4" />,
  send: <path d="M2.5 8h10m-4-4 4 4-4 4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />,
  stop: <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor" strokeWidth="0" />,
}

/**
 * An action inside an input: Listen (the frog with sound waves), Interpret
 * (with a spark) or Send. `aria-pressed` lights it while it listens.
 */
export const EcoAction = forwardRef<HTMLButtonElement, ButtonProps & { badge: keyof typeof BADGES }>(function EcoAction(
  { badge, dark, className, type = 'button', ...props },
  ref,
) {
  return (
    <button ref={ref} type={type} data-theme={dark ? 'dark' : undefined} className={cn('ecb ecb-action', className)} {...props}>
      <EcoMark className="size-[23px]" />
      <svg className="ecb-badge" viewBox="0 0 16 16" fill="none" stroke="currentColor" aria-hidden="true">
        {BADGES[badge]}
      </svg>
    </button>
  )
})

/** Eco's live face in its green circle, for a launcher or a conversation. */
export function EcoFace({ className }: { className?: string }) {
  return (
    <span className={cn('ecb-face', className)} aria-hidden="true">
      <EcoAvatar />
    </span>
  )
}

/** The launcher: the live face, a word, and the ready dot. */
export function EcoLauncher({ dark, className, children, type = 'button', ...props }: ButtonProps) {
  return (
    <button type={type} data-theme={dark ? 'dark' : undefined} className={cn('ecb ecb-launcher', className)} {...props}>
      <EcoFace />
      <span>{children}</span>
      <span className="ecb-ready" aria-hidden="true" />
    </button>
  )
}
